import { Component, computed, ElementRef, EventEmitter, HostListener, inject, Input, Output, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, firstValueFrom, map, of, shareReplay, switchMap, tap, withLatestFrom } from 'rxjs';
import { ChatMessage, Course, CourseChat, environment, PopupConfig, User } from 'src/index';
import { AuthenticationService, ChatService, CoursesService, LoadingService, UserService } from 'src/index/services.index';


@Component({
    selector: 'meta-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.css']
})
export class ChatComponent {

    userService = inject(UserService);
    loadingService = inject(LoadingService);

    @ViewChild('chatMessages') chatMessages: ElementRef | undefined;
    @Input() isGlobal: boolean = true;
    @Input() set course(c: Course | undefined) {
        if (c) {
            this.currentCourse$.next(c);
        }
    };

    @Output() doClose = new EventEmitter<void>();

    codeOfConductDisclaimerConfig: PopupConfig = {
        type: 'alert',
        size: 'large',
        labels: {
            confirm: 'Accept',
        },
        callbacks: {
            confirm: () => {
                this.loadingService.show();
                firstValueFrom(this.userService.saveData({
                    ...this.authService.$currentUser()?.data ?? {},
                    chatCodeOfConduct: true,
                }))
                    .finally(() => {
                        this.authService.$currentUser.update(user => user ? ({
                            ...user,
                            data: {
                                ...user?.data || {},
                                chatCodeOfConduct: true,
                            }
                        }) : user);
                        this.$showCodeOfConductDisclaimer.update(flag => !flag);
                        this.loadingService.hide();
                    })
            },
            cancel: () => this.chatService.toggleChat$.emit(false),
        },
        alertConfig: {
            header: {
                title: this.chatService.getTitle(),
            }
        },
        message: 'Lorem ipsum'
    }

    $codeOfConductSections = computed(() => this.chatService.getCodeOfConductText())

    $showCodeOfConductDisclaimer = signal(false);

    coursesChats$ = this.coursesService.getStudyPathCourses().pipe(
        map(courses => courses.map(course => ({ course } as CourseChat))),
        tap(courses => courses.unshift({
            course: {
                uuid: 'notifications',
                title: 'Notifiche',
                image: '',
                duration: '',
                description: '',
            }
        })),
        shareReplay(1),
    );
    currentCourse$ = new BehaviorSubject<Course | undefined>(undefined);
    $currentCourse = toSignal(this.currentCourse$);
    isCurrentChatReadOnly$ = new BehaviorSubject<boolean>(false);
    currentChat$ = this.currentCourse$.pipe(
        withLatestFrom(this.authService.currentUser$),
        switchMap(([course, currentUser]) => {
            if (course && currentUser) {
                if (environment.enableCodeOfConductDisclaimer) {
                    if (!currentUser.data?.chatCodeOfConduct) {
                        this.$showCodeOfConductDisclaimer.update(flag => !flag);
                        return of([]);
                    }
                }
                if (course.uuid === 'notifications') {
                    this.isCurrentChatReadOnly$.next(true);
                } else {
                    this.isCurrentChatReadOnly$.next(false);
                }
                this.chatService.joinRooms(currentUser, [ course.uuid ]);
                const fetchedChatMessages = this.chatService.$fetchedChatMessages().get(course.uuid);
                this.currentChatPeopleOnline = this.chatService.$currentChatPeopleOnline().get(course.uuid);
                if (fetchedChatMessages) {
                    return of(fetchedChatMessages);
                } else {
                    return of([]);
                }
            }
            return of([]);
        }),
        shareReplay(1),
        tap(() => setTimeout(() => {
            const chatEl: HTMLElement = this.chatMessages?.nativeElement;
            if (chatEl?.children?.length) {
                chatEl.children[chatEl.children.length - 1].scrollIntoView();
            }
        })),
    );
    currentChatPeopleOnline: number | undefined;

    showAllChats$ = this.currentCourse$.pipe(
        map(currentCourse => !currentCourse),
    );
    newMessage = '';

    constructor(
        public authService: AuthenticationService<User>,
        public chatService: ChatService,
        private coursesService: CoursesService,
    ) {
    }

    sendMessage(currentUser: User) {
        if (this.newMessage && this.$currentCourse()) {
            this.chatService.sendMessage(this.$currentCourse()?.uuid, currentUser, this.newMessage);
        }
        this.newMessage = '';
    }

    backToList(roomId: string) {
        if (roomId) {
            this.chatService.$notifications().set(roomId, 0);
        }
        this.currentCourse$.next(undefined);
    }

    @HostListener('window:keydown', ['$event'])
    onKeyPress($event: KeyboardEvent) {
        if ($event.key === 'Escape') {
            this.doClose.emit();
        }
    }
}
